import { useEffect, useState } from 'react';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Edit2, Plus, Store, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  code: '',
  name: '',
  address: '',
  stockIds: [],
};

const PdvLocationsPage = () => {
  const [pdvs, setPdvs] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadData = async () => {
    try {
      const [pdvResponse, stockResponse] = await Promise.all([
        api.getPdvLocations({ page: 1, limit: 100, search }),
        api.getStocks({ page: 1, limit: 100, type: 'FIXED' }),
      ]);
      setPdvs(pdvResponse.data || []);
      setStocks(stockResponse.data || []);
    } catch (error) {
      toast.error('Erro ao carregar cadastros de PDV');
    }
  };

  const openDialog = (pdv = null) => {
    setEditing(pdv);
    setFormData(pdv ? {
      code: pdv.code,
      name: pdv.name,
      address: pdv.address || '',
      stockIds: (pdv.stocks || []).map((stock) => stock.id),
    } : emptyForm);
    setDialogOpen(true);
  };

  const toggleStock = (stockId, checked) => {
    setFormData((current) => ({
      ...current,
      stockIds: checked
        ? Array.from(new Set([...current.stockIds, stockId]))
        : current.stockIds.filter((id) => id !== stockId),
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    const payload = {
      ...formData,
      address: formData.address || null,
    };
    try {
      if (editing) {
        await api.updatePdvLocation(editing.id, payload);
        toast.success('PDV atualizado');
      } else {
        await api.createPdvLocation(payload);
        toast.success('PDV cadastrado');
      }
      setDialogOpen(false);
      setEditing(null);
      setFormData(emptyForm);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao salvar PDV');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover este cadastro de PDV?')) return;
    try {
      await api.deletePdvLocation(id);
      toast.success('PDV removido');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao remover PDV');
    }
  };

  return (
    <div className="space-y-6" data-testid="pdv-locations-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Cadastro PDV</h1>
          <p className="text-muted-foreground mt-1">PDVs físicos e seus estoques fixos vinculados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Novo PDV
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Editar PDV' : 'Novo PDV'}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Código</Label>
                  <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required />
                </div>
                <div className="space-y-2">
                  <Label>NomePDV</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Endereço</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Estoques fixos vinculados</Label>
                <div className="max-h-56 overflow-y-auto rounded-md border">
                  {stocks.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Nenhum estoque fixo cadastrado.</div>
                  ) : (
                    stocks.map((stock) => (
                      <label key={stock.id} className="flex cursor-pointer items-center gap-2 border-b p-3 text-sm last:border-b-0">
                        <Checkbox
                          checked={formData.stockIds.includes(stock.id)}
                          onCheckedChange={(checked) => toggleStock(stock.id, Boolean(checked))}
                        />
                        <span className="font-mono">{stock.code}</span>
                        <span>{stock.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <Button type="submit" className="w-full">{editing ? 'Atualizar' : 'Cadastrar'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por código, NomePDV ou endereço..." searchFields={['Código', 'NomePDV', 'Endereço']} />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Código</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">NomePDV</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Endereço</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Estoques</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {pdvs.map((pdv) => (
                <tr key={pdv.id} className="border-b hover:bg-muted/50">
                  <td className="px-6 py-4 text-sm font-mono">{pdv.code}</td>
                  <td className="px-6 py-4 text-sm font-medium">{pdv.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{pdv.address || '-'}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {(pdv.stocks || []).length === 0 ? '-' : pdv.stocks.map((stock) => (
                        <Badge key={stock.id} variant="outline">{stock.code}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <Switch checked={pdv.enabled} onCheckedChange={() => api.togglePdvLocation(pdv.id).then(loadData)} />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDialog(pdv)}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(pdv.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {pdvs.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Store className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhum PDV cadastrado.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PdvLocationsPage;
